import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, formatRelative } from '../lib/api';
import { Button, Card, Alert, Spinner, Modal, Field, Input, Select, Empty } from '../components/ui';

/**
 * The recordings bucket as folders and files, and the share links made from
 * it (server: routes/recordings.ts /browse, services/shares.ts).
 */

const size = (b: number) => (b >= 1e9 ? `${(b / 1e9).toFixed(2)} GB` : b >= 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} kB`);

// ===========================================================================
// Sharing
// ===========================================================================

/** What a link covers: a folder of recordings, one file, chosen recordings (`targets`), or a tag (`target` is the tag). */
export interface ShareTarget { kind: 'folder' | 'file' | 'set' | 'tag'; target: string; targets?: string[]; name: string }

/** Make a link to a file, a recording or a folder of recordings. The link is shown once. */
export function ShareDialog({ target, onClose }: { target: ShareTarget | null; onClose: () => void }) {
  const [label, setLabel] = useState('');
  const [days, setDays] = useState('30');
  const [made, setMade] = useState<{ url: string; expiresAt: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { setLabel(target?.name ?? ''); setDays('30'); setMade(null); setError(null); setCopied(false); }, [target]);

  if (!target) return null;
  const make = async () => {
    setBusy(true); setError(null);
    try {
      setMade(await api.post<{ url: string; expiresAt: string }>('/shares', {
        kind: target.kind, label, days: Number(days),
        ...(target.kind === 'set' ? { targets: target.targets } : { target: target.target }),
      }));
    } catch (err) { setError(err instanceof ApiError ? err.message : 'Could not make the link.'); }
    finally { setBusy(false); }
  };

  return (
    <Modal
      open onClose={onClose} title={target.kind === 'file' ? 'Share a file' : target.kind === 'tag' ? `Share everything tagged #${target.target}` : target.kind === 'set' ? `Share ${target.targets?.length ?? 0} recording${target.targets?.length === 1 ? '' : 's'}` : 'Share recordings'}
      footer={made
        ? <Button variant="primary" onClick={onClose}>Done</Button>
        : <><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} onClick={() => void make()}>Make the link</Button></>}
    >
      <div className="space-y-3 text-sm">
        {error && <Alert tone="critical">{error}</Alert>}
        {!made ? (
          <>
            <p className="text-ink-secondary">
              Anyone with the link can {target.kind === 'file'
                ? 'download this file'
                : target.kind === 'tag'
                  ? `watch and download every recording tagged #${target.target} - including ones tagged later - each as a single MP4, plus its clips and transcript`
                  : `watch and download ${target.kind === 'set' ? 'these recordings' : 'every recording in this folder'} - each as a single MP4, plus any clips exported from it and its transcript`} without signing in.
              They cannot trim, export or transcribe. It can be withdrawn at any time from Shared links.
            </p>
            {target.kind === 'set'
              ? <div className="max-h-32 overflow-y-auto rounded bg-plane px-2 py-1.5 font-mono text-xs text-ink-muted">{(target.targets ?? []).map((t) => <div key={t} className="truncate">{t.replace(/^recordings\//, '')}</div>)}</div>
              : target.kind !== 'tag' && <div className="break-all rounded bg-plane px-2 py-1.5 font-mono text-xs text-ink-muted">{target.target}</div>}
            <Field label="Title" hint="Shown at the top of the page the link opens.">
              <Input value={label} maxLength={120} onChange={(e) => setLabel(e.target.value)} />
            </Field>
            <Field label="Works for">
              <Select value={days} onChange={(e) => setDays(e.target.value)}>
                {[1, 7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{d} day{d === 1 ? '' : 's'}</option>)}
              </Select>
            </Field>
            <p className="text-xs text-ink-muted">A recording is deleted when its own time runs out, whatever the link says.</p>
          </>
        ) : (
          <>
            <Alert tone="good">The link is ready. Copy it now - it is not shown again (make another if it is lost).</Alert>
            <div className="flex items-center gap-2">
              <Input readOnly value={made.url} onFocus={(e) => e.currentTarget.select()} />
              <Button onClick={() => { void navigator.clipboard.writeText(made.url).then(() => setCopied(true)); }}>{copied ? 'Copied' : 'Copy'}</Button>
            </div>
            <p className="text-xs text-ink-muted">Works until {new Date(made.expiresAt).toLocaleDateString()}.</p>
          </>
        )}
      </div>
    </Modal>
  );
}

interface Share {
  id: string; kind: string; target: string; label: string | null; createdBy: string | null; createdAt: string;
  expiresAt: string; revokedAt: string | null; uses: number; lastUsedAt: string | null; live: boolean;
}

export function SharedLinks() {
  const [shares, setShares] = useState<Share[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => api.get<{ shares: Share[] }>('/shares').then((r) => setShares(r.shares)).catch((err: Error) => setError(err.message)), []);
  useEffect(() => { void load(); }, [load]);

  if (error) return <Alert tone="critical">{error}</Alert>;
  if (!shares) return <Spinner label="Loading links…" />;
  if (!shares.length) return <Card><Empty title="No links yet">Open a recording and press Share, or share a folder or file from Files.</Empty></Card>;

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-grid text-left text-xs text-ink-muted">
              <th className="px-4 py-2 font-medium">Title</th>
              <th className="px-3 py-2 font-medium">What</th>
              <th className="px-3 py-2 font-medium">Made</th>
              <th className="px-3 py-2 font-medium">Until</th>
              <th className="px-3 py-2 font-medium">Used</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {shares.map((s) => (
              <tr key={s.id} className={`border-b border-grid ${s.live ? '' : 'text-ink-muted'}`}>
                <td className="px-4 py-2">{s.label ?? '—'}</td>
                <td className="px-3 py-2">
                  <span className="break-all font-mono text-xs">
                    {s.kind === 'tag' ? `#${s.target} (every recording with the tag)`
                      : s.kind === 'set' ? `${(() => { try { return (JSON.parse(s.target) as string[]).length; } catch { return '?'; } })()} chosen recordings`
                      : s.target.replace(/^recordings\//, '')}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">{formatRelative(s.createdAt.includes('T') ? s.createdAt : `${s.createdAt.replace(' ', 'T')}Z`)}{s.createdBy ? ` · ${s.createdBy}` : ''}</td>
                <td className="px-3 py-2 text-xs">{s.revokedAt ? 'withdrawn' : s.live ? new Date(s.expiresAt).toLocaleDateString() : 'expired'}</td>
                <td className="px-3 py-2 text-xs">{s.uses ? `${s.uses}×` : 'never'}</td>
                <td className="px-3 py-2 text-right">
                  {s.live && (
                    <button className="text-xs text-series-1 hover:underline" onClick={() => void api.delete(`/shares/${s.id}`).then(load)}>Withdraw</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ===========================================================================
// Files
// ===========================================================================

interface Folder { prefix: string; name: string; session: boolean; label?: string | null; kind?: string }
interface FileEntry { key: string; name: string; bytes: number; modified: string | null }
interface Listing { prefix: string; folders: Folder[]; files: FileEntry[]; truncated: boolean }

const prettyStamp = (s: string) => s.replace('T', '  ').replace(/-(\d{2})-(\d{2})$/, ':$1:$2');

export function FileBrowser({ onOpenSession }: { onOpenSession: (instanceId: string, session: string) => void }) {
  const [prefix, setPrefix] = useState('recordings/');
  const [listing, setListing] = useState<Listing | null>(null);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [share, setShare] = useState<ShareTarget | null>(null);

  useEffect(() => {
    setListing(null); setError(null);
    api.get<Listing>(`/recordings/browse?prefix=${encodeURIComponent(prefix)}`).then((l) => {
      setListing(l);
      // The names of the ids at the top, kept for the breadcrumb further down.
      if (l.prefix === 'recordings/') setLabels(Object.fromEntries(l.folders.filter((f) => f.label).map((f) => [f.name, f.label!])));
    }).catch((err: Error) => setError(err.message));
  }, [prefix]);

  const parts = prefix.split('/').filter(Boolean);
  const nameOf = (part: string, i: number) => (i === 0 ? 'All recordings' : i === 1 && labels[part] ? labels[part] : /T\d{2}-\d{2}-\d{2}$/.test(part) ? prettyStamp(part) : part);
  const download = async (key: string) => {
    try {
      const { url } = await api.get<{ url: string }>(`/recordings/url?key=${encodeURIComponent(key)}`);
      const a = document.createElement('a'); a.href = url; a.download = key.split('/').pop() ?? ''; a.rel = 'noopener'; a.click();
    } catch (err) { setError(err instanceof ApiError ? err.message : 'Could not get that file.'); }
  };

  const here = parts[parts.length - 1] ?? '';
  const isSession = /^\d{4}-\d{2}-\d{2}T\d{2}-\d{2}-\d{2}$/.test(here);

  return (
    <Card
      title="Files"
      action={parts.length > 1 && (
        <div className="flex gap-2">
          {isSession && <Button variant="ghost" onClick={() => onOpenSession(parts[1], here)}>Open in the player</Button>}
          <Button variant="ghost" onClick={() => setShare({ kind: 'folder', target: prefix, name: parts.slice(1).map((p, i) => nameOf(p, i + 1)).join(' / ') })}>Share this folder</Button>
        </div>
      )}
    >
      <div className="border-b border-grid px-4 py-2 text-sm">
        {parts.map((part, i) => (
          <span key={i}>
            {i > 0 && <span className="px-1.5 text-ink-muted">/</span>}
            {i === parts.length - 1
              ? <span className="font-medium">{nameOf(part, i)}</span>
              : <button className="text-series-1 hover:underline" onClick={() => setPrefix(`${parts.slice(0, i + 1).join('/')}/`)}>{nameOf(part, i)}</button>}
          </span>
        ))}
      </div>
      {error && <div className="p-4"><Alert tone="critical">{error}</Alert></div>}
      {!listing && !error && <div className="p-6"><Spinner label="Reading the folder…" /></div>}
      {listing && (
        <div className="max-h-[70vh] divide-y divide-grid overflow-auto text-sm">
          {listing.folders.length === 0 && listing.files.length === 0 && <div className="px-4 py-8 text-center text-xs text-ink-muted">Empty.</div>}
          {listing.folders.map((f) => (
            <div key={f.prefix} className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-raised/40">
              <button className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => setPrefix(f.prefix)}>
                <span aria-hidden>📁</span>
                <span className="truncate">
                  {f.label ?? (f.session ? prettyStamp(f.name) : f.name)}
                  {f.label && <span className="ml-2 font-mono text-xs text-ink-muted">{f.name}</span>}
                  {f.kind && f.kind !== 'machine' && <span className="ml-2 text-xs text-ink-muted">{f.kind}</span>}
                  {f.kind === 'machine' && !f.label && <span className="ml-2 text-xs text-ink-muted">machine no longer running</span>}
                </span>
              </button>
              <button className="text-xs text-series-1 hover:underline" onClick={() => setShare({ kind: 'folder', target: f.prefix, name: f.label ?? f.name })}>Share</button>
            </div>
          ))}
          {listing.files.map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-raised/40">
              <span className="min-w-0 flex-1 truncate"><span aria-hidden>📄</span> {f.name}</span>
              <span className="hidden text-xs text-ink-muted sm:inline">{size(f.bytes)}{f.modified ? ` · ${formatRelative(f.modified)}` : ''}</span>
              <button className="text-xs text-series-1 hover:underline" onClick={() => void download(f.key)}>Download</button>
              <button className="text-xs text-series-1 hover:underline" onClick={() => setShare({ kind: 'file', target: f.key, name: f.name })}>Share</button>
            </div>
          ))}
          {listing.truncated && <div className="px-4 py-2 text-xs text-ink-muted">Only the first few thousand are shown.</div>}
        </div>
      )}
      <ShareDialog target={share} onClose={() => setShare(null)} />
    </Card>
  );
}
